import { booleanAttribute, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import { Icons } from '../../../util/Icons';
import { AraPopoverComponent } from '../popover/popover.component';

/** Text colour of a link. `primary` = brand accent (navigation), `ink` = body text, `hint` = muted. */
export type AraLinkTone = 'primary' | 'ink' | 'hint';
/**
 * Underline behaviour. `hover` (default) underlines on hover — that is the affordance that makes a
 * link read as a link. `always` for permanently underlined meta actions, `none` only where the
 * underline would fight the layout (then the brightness change is the only hover cue).
 */
export type AraLinkUnderline = 'none' | 'hover' | 'always';

/**
 * araLink — a text hyperlink (distinct from araButton). Put it on a native `<a>`; `href` /
 * `routerLink` / focus / a11y stay native. It shows a leading icon that reflects the target:
 * a link icon for same-tab links, an open-in-new icon when it opens in a new tab.
 *
 * **Also valid on a `<button>`** — for the link-*looking* controls that don't navigate (a date
 * shortcut that fills a filter, a label that opens a panel or dialog). Those must stay real
 * buttons: an `<a>` without `href` is not focusable or keyboard-operable. On a `<button>` host the
 * destination icon is off by default, since there is no destination.
 *
 * `newTab` (boolean) opens the link in a new browser tab — it sets `target="_blank"` +
 * `rel="noopener noreferrer"` and switches the icon accordingly.
 *
 * `destinationPopover` (string) shows a hover popover naming where the link goes — pass the
 * destination's label (the same value its breadcrumb shows, e.g. `t('realestates.realestate')`),
 * NOT the raw URL. The component prepends a translated connector so the popover reads
 * "zur Liegenschaft" / "to real estate". German picks the grammatically correct preposition
 * from `destinationGender` (feminine → "zur", masculine/neuter → "zum"); English is always
 * "to". Rendered via `ara-popover` (the project's tooltip — never MatTooltip). Empty → no popover.
 *
 * `tone` picks the colour (`primary` default, `ink` for a link inside body copy that must not
 * shout, `hint` for muted meta actions). `underline` defaults to `hover` — together with the
 * brightness change that is what makes a link read as a link; `always` for muted meta actions,
 * `none` only where the underline fights the layout. `showIcon` overrides the per-host icon default.
 *
 * ```html
 * <a araLink [routerLink]="['/main/realestates', id]" [destinationPopover]="t('realestates.realestate')">{{ number }}</a>
 * <a araLink [routerLink]="[…]" destinationGender="masculine" [destinationPopover]="t('workOrders.workOrder')">{{ number }}</a>
 * <button araLink tone="ink" underline="hover" type="button" (click)="setLastMonth()">{{ t('…lastMonth') }}</button>
 * <button araLink type="button" (click)="openPanel()">{{ realEstate.name }}</button>
 * ```
 */
@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'a[araLink], button[araLink]',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	imports: [MatIcon, AraPopoverComponent],
	templateUrl: './ara-link.component.html',
	styleUrl: './ara-link.component.scss',
	host: {
		class: 'ara-link',
		'[attr.target]': "newTab() ? '_blank' : null",
		'[attr.rel]': "newTab() ? 'noopener noreferrer' : null",
		'[class.ara-link--primary]': "tone() === 'primary'",
		'[class.ara-link--ink]': "tone() === 'ink'",
		'[class.ara-link--hint]': "tone() === 'hint'",
		'[class.ara-link--underline-hover]': "underline() === 'hover'",
		'[class.ara-link--underline-always]': "underline() === 'always'",
	},
})
export class AraLinkComponent {
	private readonly icons = inject(Icons);
	private readonly transloco = inject(TranslocoService);
	private readonly hostTag = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement.tagName.toLowerCase();

	/** Open the link in a new tab (adds target/rel and shows the open-in-new icon). */
	readonly newTab = input(false, { transform: booleanAttribute });
	/** Destination label (breadcrumb value) the popover names, e.g. `t('realestates.realestate')`. Empty → no popover. */
	readonly destinationPopover = input('');
	/** Grammatical gender of the destination noun — drives the German preposition (feminine → "zur", masculine/neuter → "zum"). */
	readonly destinationGender = input<'feminine' | 'masculine' | 'neuter'>('feminine');
	/** Text colour. */
	readonly tone = input<AraLinkTone>('primary');
	/** Underline emphasis. Defaults to `hover` — a link should show that it is one. */
	readonly underline = input<AraLinkUnderline>('hover');
	/**
	 * Show the leading destination icon. Defaults to `true` on an `<a>` (it names the target) and to
	 * `false` on a `<button>` (nothing to navigate to) — set it explicitly to override either.
	 */
	readonly showIcon = input<boolean | undefined, boolean | string | undefined>(undefined, {
		transform: (value) => (value === undefined ? undefined : booleanAttribute(value)),
	});

	protected readonly iconName = computed(() => this.icons.get(this.newTab() ? 'openInNew' : 'link'));
	protected readonly renderIcon = computed(() => this.showIcon() ?? this.hostTag === 'a');

	/** Translated connectors ("zur" / "zum", both "to" in English); react to language changes. */
	private readonly goToFeminine = toSignal(this.transloco.selectTranslate('commonActions.goTo'), { initialValue: '' });
	private readonly goToMasculine = toSignal(this.transloco.selectTranslate('commonActions.goToM'), { initialValue: '' });

	/** Full popover text: "<zur/zum/to> <destination label>". Empty when no destination is set. */
	protected readonly popoverContent = computed(() => {
		const destination = this.destinationPopover();
		if (!destination) {
			return '';
		}
		const prefix = this.destinationGender() === 'feminine' ? this.goToFeminine() : this.goToMasculine();
		return prefix ? `${prefix} ${destination}` : destination;
	});
}
